import { Link, router } from '@inertiajs/react'
import { Plus, ClipboardList } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { TaskTypeSummary } from '@/types'

type Props = {
  types: TaskTypeSummary[]
  search: string
  pagination: PaginationMeta
}

export default function TaskTypesIndex({ types, search, pagination }: Props) {
  return (
    <BackofficeLayout title="Task Types" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Task Types' }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Task Types</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? 'type' : 'types'}</p>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" asChild className="gap-1.5 text-[13px] h-8">
            <Link href="/backoffice/task_types/new">
              <Plus className="w-3.5 h-3.5" />
              New Type
            </Link>
          </Button>
        </div>
      </div>

      <SearchInput value={search} placeholder="Search task types..." />

      <div className="mt-4">
        {types.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <ClipboardList className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No task types configured yet</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {types.map((t, i) => (
              <div
                key={t.id}
                className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                onClick={() => router.visit(`/backoffice/task_types/${t.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link href={`/backoffice/task_types/${t.id}`} className="text-sm font-medium hover:underline">{t.name}</Link>
                      <span className="text-[12px] text-muted-foreground font-mono">{t.code}</span>
                    </div>
                    {t.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</p>}
                  </div>
                  <div className="text-xs text-muted-foreground ml-4">
                    {t.fields.length} field{t.fields.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
